import {decorate, observable, computed, action} from "mobx";
import get from "lodash.get";
import template from "lodash.template";

import copy from "./copy";
import {submitDataForFlowApplication} from "../../utils/form-submission";
import {initialFieldStates} from "./field-states";
import {addPartnerSpecificApplicationData, formatVehicleData} from "../../utils/submission-helpers";
import {isWindowDefined} from '../../../utils/general-utils';
import {validateIntegerString} from '../../../utils/validation';
import { FLOW_NAMES } from "../../../enums/flows";
import {
    EMPLOYMENT_FIELD_RULES,
    PREVIOUS_EMPLOYMENT_FIELD_RULES,
    getFieldConditionality,
    getPreviousFieldConditionality,
} from "./employment-field-rules";
import {
    PREVIOUS_ADDRESS_FIELDS,
    PREVIOUS_EMPLOYMENT_FIELDS,
    REQUIRED_PREVIOUS_ADDRESS_FIELDS,
    formatDigitalCreditApplicationData,
    isWithinPreviousHistoryWindow,
} from "./applicant-history";

// field-state templates in field-states.js are shared module-level objects;
// clone them so in-place value/error updates don't mutate the templates
const cloneFieldStates = (fieldStates) => JSON.parse(JSON.stringify(fieldStates));

const DEFAULT_PURCHASE_INTENT = "NOT_PROVIDED";
const DEFAULT_CONTACT_PREFERENCE = "contact_by_not_provided";

const isEmptyFieldValue = (value) => {
    if (!value) return true;
    if (typeof value !== "object") return false;
    return Object.values(value).every((part) => !part);
};

export class DigitalCreditAppStepStore {
    getFieldValue = (fieldName, defaultValue) => (this.fieldStates[fieldName]?.value ?? defaultValue);

    getEmploymentStatus = () => this.getFieldValue("employment_status", "");

    getPreviousEmploymentStatus = () => this.getFieldValue("previous_employment_status", "");

    resetInitialFieldStates = () => {
        this.fieldStates = cloneFieldStates(initialFieldStates);
        this.interactedFields = {};
        this.validationAttempted = false;
        this.textConsent = null;
        this.prequalDisclosureConsent = null;
    }

    updateFieldValue = (value, fieldName) => {
        this.interactedFields[fieldName] = true;
        this.fieldStates[fieldName] = value;
        if (["other_income_source", "other_income_gross_amount"].includes(fieldName)) {
            this.validateOtherIncomeSourceAndAmount();
        }
        if (["previous_residential_years", "previous_residential_months"].includes(fieldName)) {
            this.validatePreviousAddress();
        }
        if (["previous_employer_years", "previous_employer_months"].includes(fieldName)) {
            this.validatePreviousEmployment();
        }
    };

    clearField = (fieldName) => {
        if (!fieldName || !this.fieldStates[fieldName]) return;
        const defaultValue = initialFieldStates[fieldName]?.value ?? null;
        this.fieldStates[fieldName] = { value: defaultValue, error: false };
    };

    clearFields = (fieldNames) => fieldNames.forEach(this.clearField);

    markEmptyRequiredFieldsInvalid = (fieldNames) => {
        fieldNames.forEach((fieldName) => {
            const fieldState = this.fieldStates[fieldName];
            if (fieldState && isEmptyFieldValue(fieldState.value)) {
                fieldState.error = true;
            }
        });
    };

    onResidentialDateChange = (value) => {
        this.updateFieldValue(value, "residential_date");
        if (this.hasLivedHereForLessThanTwoYears) {
            this.validatePreviousAddress();
        } else {
            this.clearFields(PREVIOUS_ADDRESS_FIELDS);
        }
    };

    onEmployerDateChange = (value) => {
        this.updateFieldValue(value, "employer_date");
        if (this.hasWorkedHereForLessThanTwoYears) {
            this.validatePreviousEmployment();
        } else {
            this.clearFields(PREVIOUS_EMPLOYMENT_FIELDS);
        }
    };

    onEmploymentStatusChange = (event) => {
        this.updateFieldValue(event, "employment_status");
        this.clearDisabledEmploymentFields();
        this.revalidateEmploymentFields();
        if (!this.hasWorkedHereForLessThanTwoYears) {
            this.clearFields(PREVIOUS_EMPLOYMENT_FIELDS);
        }
    };

    onPreviousEmploymentStatusChange = (event) => {
        this.updateFieldValue(event, "previous_employment_status");
        this.clearDisabledPreviousEmploymentFields();
        this.revalidatePreviousEmploymentFields();
    };

    clearDisabledEmploymentFields = () => {
        const status = this.getEmploymentStatus();
        if (!status) return;
        Object.keys(EMPLOYMENT_FIELD_RULES).forEach((fieldName) => {
            if (EMPLOYMENT_FIELD_RULES[fieldName].disabled.includes(status)) {
                this.clearField(fieldName);
            }
        });
    };

    clearDisabledPreviousEmploymentFields = () => {
        const status = this.getPreviousEmploymentStatus();
        if (!status) return;
        Object.keys(PREVIOUS_EMPLOYMENT_FIELD_RULES).forEach((fieldName) => {
            if (PREVIOUS_EMPLOYMENT_FIELD_RULES[fieldName].disabled.includes(status)) {
                this.clearField(fieldName);
            }
        });
    };

    revalidateEmploymentFields = () => {
        const status = this.getEmploymentStatus();
        Object.keys(EMPLOYMENT_FIELD_RULES).forEach((fieldName) => {
            const fieldState = this.fieldStates[fieldName];
            if (!fieldState) return;

            const {disabled, required} = getFieldConditionality(fieldName, status);
            if (disabled) return;

            // A status transition changes whether an empty field is allowed.
            // Preserve errors for non-empty values so field-specific validation
            // (phone/date/etc.) is not bypassed.
            if (isEmptyFieldValue(fieldState.value)) {
                fieldState.error = required;
            }
        });
    };

    revalidatePreviousEmploymentFields = () => {
        const status = this.getPreviousEmploymentStatus();
        Object.keys(PREVIOUS_EMPLOYMENT_FIELD_RULES).forEach((fieldName) => {
            const fieldState = this.fieldStates[fieldName];
            if (!fieldState) return;

            const {disabled, required} = getPreviousFieldConditionality(fieldName, status);
            if (disabled) return;

            if (isEmptyFieldValue(fieldState.value)) {
                fieldState.error = required;
            }
        });
    };

    replaceFieldValues(values){
        Object.keys(values).forEach((fieldName) => {
            if(this.fieldStates[fieldName]){
                this.fieldStates[fieldName].value = values[fieldName];
            }
        });
        this.clearDisabledEmploymentFields();
        this.clearDisabledPreviousEmploymentFields();
        this.revalidatePreviousEmploymentFields();
    }

    submitForm = async (captcha, vehicleConfiguration, rawVehicleData, updateApplicationUuid, updateApplicantUuids) => {
        let data = formatDigitalCreditApplicationData(this.fieldStates, {
            includePreviousAddress: this.hasLivedHereForLessThanTwoYears,
            includePreviousEmployment: this.hasWorkedHereForLessThanTwoYears,
        });
        data.captcha = captcha;
        data.meta_data =  {};

        if (this.historyStore.queryParams.dealership) {
            data.consumer_dealership_identifier = this.historyStore.queryParams.dealership;
            data.dealership = this.historyStore.queryParams.dealership;
        }

        if (this.historyStore.queryParams.a) {
            data.meta_data.assigned_link = this.historyStore.queryParams.a;
        }

        if (this.dealershipStore.externalDealershipId) {
            data.external_dealership_id = this.dealershipStore.externalDealershipId;
        }

        if (this.themeStore.theme) {
            data.meta_data.appTheme = this.themeStore.serializedTheme || this.themeStore.theme;
        }

        if(this.partnerStore){
            data = addPartnerSpecificApplicationData(data, this.partnerStore);
        }

        if (vehicleConfiguration) {
            data.vehicle = vehicleConfiguration.uuid;
        }

        if (rawVehicleData) {
            data.vehicle_data = formatVehicleData(rawVehicleData, "vss");
        } else if(this.partnerStore.rawVehicle){
            data.vehicle_data = formatVehicleData(this.partnerStore.rawVehicle, "widget");
        }

        if (data?.vehicle_data?.dealer_id) {
            data.octane_dealership_id = data.vehicle_data.dealer_id;
        } else {
            data.octane_dealership_id = '';
        }

        if (data?.vehicle_data?.cta_type) {
            data.cta_type = data.vehicle_data.cta_type;
        // as the vehicle selection CTAs (banner and sidebar) don't
        // receive the dum-e message, we pass the cta type through the query params
        } else if (this.historyStore.queryParams.source) {
            data.cta_type = this.historyStore.queryParams.source;
        }

        data.purchase_intent = DEFAULT_PURCHASE_INTENT;
        data.contact_preference = DEFAULT_CONTACT_PREFERENCE;

        const flowUuid = this.historyStore.queryParams.flowUuid;
        if (flowUuid) data.flow_uuid = flowUuid;

        const queryParams = this.historyStore.queryParams;
        if (queryParams.inStore || queryParams.instore) {
            data.meta_data.inStore = true;
        }

        /* If coapplicant intent is enabled and the coapplicant intent field is set */
        if(!this.flowStore.isCoapplicantFlow && this.partnerStore?.isCoapplicantEnabled && data.coapplicant_intent){
            /* Saves address data to be reused by coapplicant */
            this.primaryApplicantAddressData = {
                street1: data.street1,
                street2: data.street2,
                city: data.city,
                state: data.state,
                zip_code: data.zip_code,
            }
            this.resetInitialFieldStates();
        }
        try{
            const response = await submitDataForFlowApplication(FLOW_NAMES.DIGITAL_CREDIT_APP, data);
            if (response.success) {
                const applicationUuid = response.data['flow_uuid'];
                const primaryApplicantUuid = response.data['primary_applicant_uuid'];
                const flowStepName = response.data['step_name'];

                updateApplicationUuid(applicationUuid);
                updateApplicantUuids({primaryApplicantUuid});
                this.flowStore.handleStepRedirection(flowStepName);
                this.resetInitialFieldStates();
            } else {
                const errorMessage = `Server error ${response.status}`;
                this.showServerErrorView(errorMessage)
            }
        } catch (error){
            this.showServerErrorView(error)
        }
    };

    showServerErrorView(message){
        this.serverErrorMessage = message;

        // This must be the last statement to avoid multiple rendering of the ServerError view
        this.showServerError = true;
    }

    getFieldConditionalityFor = (fieldName) => (
        PREVIOUS_EMPLOYMENT_FIELDS.includes(fieldName)
            ? getPreviousFieldConditionality(fieldName, this.getPreviousEmploymentStatus())
            : getFieldConditionality(fieldName, this.getEmploymentStatus())
    );

    forceFieldsValidation = () => {
        this.validationAttempted = true;
        this.getFieldNames.forEach((fieldName) => {
            if (!this.isHistoryFieldActive(fieldName)) return;
            // Disabled fields are cleared and greyed out — skip focus/blur.
            if (this.getFieldConditionalityFor(fieldName).disabled) return;

            const field = document.getElementById(fieldName);
            const isSelect = field && field.dataset && field.dataset["oid"] === "select";
            if(isSelect){
                // Select fields doesn't trigger blur events, so we validate required manually
                this.fieldStates[fieldName].error = !this.fieldStates[fieldName].value;
            } else {
                // For other types, trigger automatic validation by forcing blur event
                field && field.focus({preventScroll: true});
                field && field.blur();
            }
        });
        this.validateOtherIncomeSourceAndAmount();
        this.validateApplicantHistory();
    };

    isHistoryFieldActive = (fieldName) => {
        if (PREVIOUS_ADDRESS_FIELDS.includes(fieldName)) {
            return this.hasLivedHereForLessThanTwoYears;
        }
        if (PREVIOUS_EMPLOYMENT_FIELDS.includes(fieldName)) {
            return this.hasWorkedHereForLessThanTwoYears;
        }
        return true;
    };

    validatePreviousAddress = () => {
        if (!this.hasLivedHereForLessThanTwoYears) return;
        this.markEmptyRequiredFieldsInvalid(REQUIRED_PREVIOUS_ADDRESS_FIELDS);

        const years = this.getFieldValue("previous_residential_years", "");
        const months = this.getFieldValue("previous_residential_months", "");
        this.fieldStates.previous_residential_years.error = !validateIntegerString(years, 0, 99);
        this.fieldStates.previous_residential_months.error = !validateIntegerString(months, 0, 11);
    };

    validatePreviousEmployment = () => {
        if (!this.hasWorkedHereForLessThanTwoYears) return;
        const status = this.getPreviousEmploymentStatus();

        PREVIOUS_EMPLOYMENT_FIELDS.forEach((fieldName) => {
            const fieldState = this.fieldStates[fieldName];
            if (!fieldState) return;

            const {disabled, required} = getPreviousFieldConditionality(fieldName, status);
            if (disabled) return;

            if (required && isEmptyFieldValue(fieldState.value)) {
                fieldState.error = true;
            }
        });

        // Duration presence/positive-total checks only apply when the previous
        // tenure is required. When it is disabled (retired/student/unemployed)
        // the fields are cleared, so leave their error state alone.
        const tenureRequired = getPreviousFieldConditionality(
            "previous_employer_years",
            status,
        ).required;
        if (!tenureRequired) return;

        const years = this.getFieldValue("previous_employer_years", "");
        const months = this.getFieldValue("previous_employer_months", "");
        const yearsAreValid = validateIntegerString(years, 0, 99);
        const monthsAreValid = validateIntegerString(months, 0, 11);
        const durationIsPositive = !yearsAreValid || !monthsAreValid
            || (Number(years) * 12) + Number(months) > 0;

        this.fieldStates.previous_employer_years.error = !yearsAreValid || !durationIsPositive;
        this.fieldStates.previous_employer_months.error = !monthsAreValid || !durationIsPositive;
    };

    validateApplicantHistory = () => {
        this.validatePreviousAddress();
        this.validatePreviousEmployment();
    };

    get otherIncomeSourceAndAmountMeetRequirements() {
        const source = this.getFieldValue("other_income_source", "").trim();
        const amount = this.getFieldValue("other_income_gross_amount", "");
        const amountIsValid = amount === "" || validateIntegerString(amount, 1);
        return ((source !== "") === (amount !== "")) && amountIsValid;
    }

    validateOtherIncomeSourceAndAmount = () => {
        const source = this.getFieldValue("other_income_source", "").trim();
        const amount = this.getFieldValue("other_income_gross_amount", "");
        const sourceIsPresent = source !== "";
        const amountIsPresent = amount !== "";
        const amountIsValid = !amountIsPresent || validateIntegerString(amount, 1);

        this.fieldStates.other_income_source.error = amountIsPresent && !sourceIsPresent;
        this.fieldStates.other_income_gross_amount.error = !amountIsValid
            || (sourceIsPresent && !amountIsPresent);
    };

    onReusePrimaryAddressChange(state){
        this.updateFieldValue(state, "reuse_primary_address");
        if(this.primaryApplicantAddressData){
            this.replaceFieldValues(this.primaryApplicantAddressData);
        }
    }

    getVisibleFieldError = (fieldName) => {
        const fieldState = this.fieldStates[fieldName];
        return !!(fieldState && fieldState.error && (this.validationAttempted || this.interactedFields[fieldName]));
    };

    updatePrequalDisclosureConsent = (prequalDisclosureConsent) => {
        this.prequalDisclosureConsent = prequalDisclosureConsent;
        this.forceFieldsValidation();
    };

    updateTextConsent = (textConsent) => {
        this.textConsent = textConsent;
    };

    get getFieldNames() {
        return Object.keys(this.fieldStates);
    }

    get employmentFieldConditionality() {
        const status = this.getEmploymentStatus();
        return Object.keys(EMPLOYMENT_FIELD_RULES).reduce((acc, fieldName) => {
            acc[fieldName] = getFieldConditionality(fieldName, status);
            return acc;
        }, {});
    }

    get previousEmploymentFieldConditionality() {
        const status = this.getPreviousEmploymentStatus();
        const groupIsVisible = this.hasWorkedHereForLessThanTwoYears;
        return Object.keys(PREVIOUS_EMPLOYMENT_FIELD_RULES).reduce((acc, fieldName) => {
            acc[fieldName] = groupIsVisible
                ? getPreviousFieldConditionality(fieldName, status)
                : {disabled: false, required: false};
            return acc;
        }, {});
    }

    get hasLivedHereForLessThanTwoYears() {
        return isWithinPreviousHistoryWindow(this.getFieldValue("residential_date", null));
    }

    get hasWorkedHereForLessThanTwoYears() {
        const employerDateIsDisabled = getFieldConditionality(
            "employer_date",
            this.getEmploymentStatus(),
        ).disabled;
        return !employerDateIsDisabled
            && isWithinPreviousHistoryWindow(this.getFieldValue("employer_date", null));
    }

    get previousAddressMeetsRequirements() {
        if (!this.hasLivedHereForLessThanTwoYears) return true;
        const requiredFieldsArePresent = REQUIRED_PREVIOUS_ADDRESS_FIELDS.every(
            (fieldName) => !isEmptyFieldValue(this.fieldStates[fieldName].value),
        );
        return requiredFieldsArePresent
            && validateIntegerString(this.getFieldValue("previous_residential_years", ""), 0, 99)
            && validateIntegerString(this.getFieldValue("previous_residential_months", ""), 0, 11);
    }

    get previousEmploymentMeetsRequirements() {
        if (!this.hasWorkedHereForLessThanTwoYears) return true;

        // The previous status itself is required while the group is shown.
        if (isEmptyFieldValue(this.getFieldValue("previous_employment_status", ""))) {
            return false;
        }

        const status = this.getPreviousEmploymentStatus();
        const requiredFieldsArePresent = PREVIOUS_EMPLOYMENT_FIELDS.every((fieldName) => {
            const {disabled, required} = getPreviousFieldConditionality(fieldName, status);
            if (disabled || !required) return true;
            return !isEmptyFieldValue(this.fieldStates[fieldName].value);
        });
        if (!requiredFieldsArePresent) return false;

        const tenureRequired = getPreviousFieldConditionality(
            "previous_employer_years",
            status,
        ).required;
        if (!tenureRequired) return true;

        const years = this.getFieldValue("previous_employer_years", "");
        const months = this.getFieldValue("previous_employer_months", "");
        return validateIntegerString(years, 0, 99)
            && validateIntegerString(months, 0, 11)
            && (Number(years) * 12) + Number(months) > 0;
    }

    get isFormValid() {
        const fieldsAreValid = this.getFieldNames.reduce((agg, fieldName) => {
            if (!this.isHistoryFieldActive(fieldName)) return agg;
            // Defensive: even though disabled fields are cleared, they should
            // never block submission regardless of any stale error state.
            if (this.getFieldConditionalityFor(fieldName).disabled) return agg;
            return agg && !this.fieldStates[fieldName].error;
        }, true);
        return fieldsAreValid
            && this.otherIncomeSourceAndAmountMeetRequirements
            && this.previousAddressMeetsRequirements
            && this.previousEmploymentMeetsRequirements;
    }

    get formHeaderTitle() {
         return this.uxCopyStore.getAboutYouCopy("title") || get(copy, "title", "");
    }

    get continueButtonLabel() {
        const hasCoapplicantIntent = this.getFieldValue("coapplicant_intent", false);
        if (hasCoapplicantIntent) {
            return this.uxCopyStore.getAboutYouCopy("continue_button.add_coapplicant_label")
                || get(copy, "continue_button.add_coapplicant_label", "");
        }
        return this.uxCopyStore.getAboutYouCopy("continue_button.submit_application_label")
            || get(copy, "continue_button.submit_application_label", "");
    }

    get showCoapplicantIntentField(){
        return !this.flowStore.isCoapplicantFlow && this.partnerStore.isCoapplicantEnabled;
    }

    get isReusingPrimaryAddress() {
        return this.fieldStates["reuse_primary_address"]?.value || false;
    }

    prequalDisclosureConsent = false;
    /** Note: Name and privacy policy URL must be filled out at the partner and/or dealership level to add additional policy */
    get prequalDisclosure() {
        const dealership = this.dealershipStore.dealership;
        const partner = this.partnerStore.partner;
        let name, privacyPolicyURL;

        // if dealership is populated and it has privacy_policy_url set, use it to construct the disclosure
        if (dealership && dealership.name && dealership.privacy_policy_url) {
            name = dealership.name;
            privacyPolicyURL = dealership.privacy_policy_url;
        }
        // if partner is populated and it has privacy_policy_url set, use it to construct the disclosure
        else if (partner && partner.name && partner.privacy_policy_url) {
            name = partner.name;
            privacyPolicyURL = partner.privacy_policy_url;
        }

            // if enable_alternate_lenders enabled, need to render markdown with alternate lenders disclosure
        if (partner && get(partner, 'enable_alternate_lenders')) {
                return template(copy.prequal_disclosure.alternate_lenders_markdown_content)({
                    continueButtonLabel: this.continueButtonLabel,
                    name,
                    privacyPolicyURL,
                });
            }

        return template(copy.prequal_disclosure.markdown_content)({
            continueButtonLabel: this.continueButtonLabel,
            name,
            privacyPolicyURL,
        });
    }

    constructor({
        historyStore,
        themeStore,
        uxCopyStore,
        dealershipStore,
        partnerStore,
        rideOctaneStore,
        flowStore,
        ...props
    }) {
        this.uxCopyStore = uxCopyStore;
        this.dealershipStore = dealershipStore;
        this.partnerStore = partnerStore;
        this.historyStore = historyStore;
        this.themeStore = themeStore;
        this.rideOctaneStore = rideOctaneStore;
        this.flowStore = flowStore;

        this.textConsent = false;
        this.prequalDisclosureConsent = false;
        this.primaryApplicantAddressData = null;
        this.interactedFields = {};
        this.validationAttempted = false;

        this.showServerError = false;
        this.serverErrorMessage = "";

        this.fieldStates = cloneFieldStates(initialFieldStates);
        this.clearDisabledEmploymentFields();

        if(isWindowDefined()){
            window.replaceFieldValues = (values) => this.replaceFieldValues(values)
        }
    }
}

decorate(DigitalCreditAppStepStore, {
    fieldStates: observable,
    prequalDisclosureConsent: observable,
    textConsent: observable,
    showServerError: observable,
    serverErrorMessage: observable,
    primaryApplicantAddressData: observable,
    interactedFields: observable,
    validationAttempted: observable,

    getFieldNames: computed,
    isFormValid: computed,
    otherIncomeSourceAndAmountMeetRequirements: computed,
    hasLivedHereForLessThanTwoYears: computed,
    hasWorkedHereForLessThanTwoYears: computed,
    previousAddressMeetsRequirements: computed,
    previousEmploymentMeetsRequirements: computed,
    employmentFieldConditionality: computed,
    previousEmploymentFieldConditionality: computed,
    showCoapplicantIntentField: computed,
    continueButtonLabel: computed,
    prequalDisclosure: computed,

    updateFieldValue: action,
    forceFieldsValidation: action,
    onReusePrimaryAddressChange: action,
    updatePrequalDisclosureConsent: action,
    updateTextConsent: action,
    resetInitialFieldStates: action,
    clearField: action,
    clearFields: action,
    onResidentialDateChange: action,
    onEmployerDateChange: action,
    onEmploymentStatusChange: action,
    onPreviousEmploymentStatusChange: action,
    clearDisabledEmploymentFields: action,
    clearDisabledPreviousEmploymentFields: action,
    revalidateEmploymentFields: action,
    revalidatePreviousEmploymentFields: action,
    validateOtherIncomeSourceAndAmount: action,
    validatePreviousAddress: action,
    validatePreviousEmployment: action,
    validateApplicantHistory: action,
    replaceFieldValues: action,
});
